import * as React from 'react'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import useStationStore from '../../store/station'
import useUserSettingsStore from '../../store/userSettings'
import { getFuzzyText } from '../../utils/fuzzyTime'
import { formatLiteralTime } from '../common/ClockWidget'

const CLOCK_MIN_CH = 40

function padTwo(value: number) {
  return value.toString().padStart(2, '0')
}

export default function HeaderClock() {
  const theme = useTheme()
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const fuzzyClockEnabled = useUserSettingsStore((state) => state.clockFuzzy)
  const activeStationTimezone = useStationStore((state) => state.activeStation?.timezone)

  const browserTimezone = React.useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch {
      return undefined
    }
  }, [])

  const timezone = activeStationTimezone || browserTimezone || 'UTC'

  const getClockParts = React.useMemo(() => {
    let formatter: Intl.DateTimeFormat
    try {
      formatter = new Intl.DateTimeFormat([], {
        timeZone: timezone,
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    } catch {
      formatter = new Intl.DateTimeFormat([], {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    }

    return () => {
      const parts = formatter.formatToParts(new Date())
      const result: Record<'hour' | 'minute' | 'second', string> = {
        hour: '00',
        minute: '00',
        second: '00',
      }

      for (const part of parts) {
        if (part.type === 'hour' || part.type === 'minute' || part.type === 'second') {
          result[part.type] = part.value
        }
      }

      return result
    }
  }, [timezone])

  const [, force] = React.useReducer((x: number) => x + 1, 0)
  React.useEffect(() => {
    const id = window.setInterval(force, 1000)
    return () => window.clearInterval(id)
  }, [])

  const { hour, minute, second } = getClockParts()
  const hoursNum = Number.parseInt(hour, 10) || 0
  const minutesNum = Number.parseInt(minute, 10) || 0
  const secondsNum = Number.parseInt(second, 10) || 0

  const stationClock = React.useMemo(() => {
    const date = new Date()
    date.setHours(hoursNum, minutesNum, secondsNum, 0)
    return date
  }, [hoursNum, minutesNum, secondsNum])

  const digital = `${padTwo(hoursNum)}:${padTwo(minutesNum)}:${padTwo(secondsNum)}`
  const preciseText = formatLiteralTime(stationClock)
  const fuzzyText = getFuzzyText(stationClock, { preferJustGone: true })
  const text = fuzzyClockEnabled
    ? fuzzyText || `It’s ${digital}`
    : preciseText || `It’s ${digital}`

  const remaining = 60 - secondsNum
  const pct = (remaining / 60) * 100

  const barBg =
    theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300]

  const barFill =
    theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main

  return (
    <Box
      className="rpa-clock"
      sx={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
      }}
      aria-label="Clock"
    >
      <Box
        sx={{
          minWidth: `${CLOCK_MIN_CH}ch`,
          maxWidth: { xs: `${CLOCK_MIN_CH}ch`, md: '48ch' },
          mx: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.25,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontFamily: 'monospace', lineHeight: 1 }}
          aria-live="off"
        >
          {digital}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: { xs: 14, md: 16 },
            lineHeight: 1.3,
            fontWeight: 500,
            textTransform: 'none',
            letterSpacing: 0,
            textAlign: 'center',
            whiteSpace: 'nowrap',
            m: 0,
            p: 0,
          }}
          aria-live="polite"
        >
          {text}
        </Typography>

        <Box
          role="progressbar"
          aria-label="Seconds remaining in the minute"
          aria-valuemin={0}
          aria-valuemax={60}
          aria-valuenow={remaining}
          sx={{
            mt: 0.5,
            width: '100%',
            height: 6,
            borderRadius: 999,
            backgroundColor: barBg,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: `${pct}%`,
              height: '100%',
              backgroundColor: barFill,
              transition: prefersReducedMotion
                ? 'none'
                : theme.transitions.create('width', {
                    duration: 1000,
                    easing: 'linear',
                  }),
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}
